import { MediaPreview, PendingMediaPreview } from "../components/review/MediaPreview";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useBrand } from "../hooks/useBrand";
import { Button } from "../components/ui/Button";
import {
  useCollections,
  useDeleteMediaAsset,
  useEstimateShopifyImport,
  useInfiniteMediaList,
  useImportMediaUrls,
  useRenderMediaReferences,
  useShopifyIntegrations,
  useConfirmShopifyImport,
  useUpdateMediaAsset,
  useUploadMedia,
} from "../hooks/useMedia";
import { CANONICAL_ASPECT_RATIOS, type AssetCollection, type MediaAsset } from "../../../shared/types";
import { WorkspaceHeader } from "../components/layout/WorkspaceHeader";
import { Modal } from "../components/ui/Modal";
import { buildAccountConnectionsPath } from "../lib/brand-navigation";
import { formatCents } from "../lib/utils";
import { api, ApiError, apiUrl } from "../lib/api";
import { showToast } from "../lib/toast";

const SUITABLE_FOR_OPTIONS = ["email_header", "product_listing", "social_square"] as const;
const USAGE_RIGHTS_OPTIONS = ["owned", "licensed", "ugc_approved", "ugc_pending", "unknown"] as const;
const ASSET_TYPE_OPTIONS = ["product_shot", "lifestyle", "ugc", "ad_creative", "brand_kit", "social", "other"] as const;
const MAX_IMPORT_URLS = 50;
const ACCEPTED_UPLOAD_MIME_TYPES = ["image/png", "image/jpeg", "image/webp", "application/pdf", "video/mp4"] as const;

function safePreviewHtml(content: string): string {
  if (typeof DOMParser === "undefined") {
    return content
      .replace(/<script\b[\s\S]*?<\/script>/gi, "")
      .replace(/\son[a-z]+\s*=\s*(?:"[^"]*"|'[^']*'|[^\s>]+)/gi, "")
      .replace(/\s(?:src|href)\s*=\s*(?:["'](?:https?:\/\/|\/\/|javascript:)[^"']*["']|(?:https?:\/\/|\/\/|javascript:)[^\s>]+)/gi, "");
  }

  const doc = new DOMParser().parseFromString(content, "text/html");
  for (const node of Array.from(doc.querySelectorAll("*"))) {
    if (node.tagName.toLowerCase() === "script") {
      node.remove();
      continue;
    }
    for (const attr of Array.from(node.attributes)) {
      const name = attr.name.toLowerCase();
      const value = attr.value.trim().toLowerCase();
      if (name.startsWith("on")) {
        node.removeAttribute(attr.name);
        continue;
      }
      if ((name === "src" || name === "href") && (value.startsWith("http://") || value.startsWith("https://") || value.startsWith("//") || value.startsWith("javascript:"))) {
        node.removeAttribute(attr.name);
      }
    }
  }

  return doc.body.innerHTML;
}

export function AssetLibrary() {
  // Under the WorkspaceShell there is no BrandLayout outlet context, so read the
  // brand from the route param + useBrand (mirrors pages-v2/BrandDocsPage). The
  // hooks below require a string brandId; we coerce here and gate render on the
  // missing/loading/error guards just before the main return.
  const { brandId: brandIdParam } = useParams<{ brandId: string }>();
  const brandId = brandIdParam ?? "";
  const { data: brandDetail, isLoading: brandLoading, isError: brandError } = useBrand(brandIdParam);
  const brand = brandDetail?.data ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const listParams = useMemo(() => {
    const params = new URLSearchParams();
    params.set("limit", "50");
    const q = searchParams.get("q");
    const assetType = searchParams.get("asset_type");
    const collectionId = searchParams.get("collection_id");
    const aspectRatio = searchParams.get("aspect_ratio");
    const suitableFor = searchParams.get("suitable_for");
    const usageRights = searchParams.get("usage_rights");
    if (q) params.set("q", q);
    if (assetType) params.set("asset_type", assetType);
    if (collectionId) params.set("collection_id", collectionId);
    if (aspectRatio) params.set("aspect_ratio", aspectRatio);
    if (suitableFor) params.set("suitable_for", suitableFor);
    if (usageRights) params.set("usage_rights", usageRights);
    return params;
  }, [searchParams]);

  const mediaQuery = useInfiniteMediaList(brandId, listParams);
  const collectionsQuery = useCollections(brandId);
  const upload = useUploadMedia(brandId);
  const importUrls = useImportMediaUrls(brandId);
  const updateAsset = useUpdateMediaAsset(brandId);
  const deleteAsset = useDeleteMediaAsset(brandId);
  const renderPreview = useRenderMediaReferences(brandId);
  const shopifyIntegrations = useShopifyIntegrations(brandId);
  const estimateShopifyImport = useEstimateShopifyImport(brandId);
  const confirmShopifyImport = useConfirmShopifyImport(brandId);

  const [selectedAssetId, setSelectedAssetId] = useState<string | null>(null);
  const [optimisticAssets, setOptimisticAssets] = useState<Record<string, Partial<MediaAsset>>>({});
  const [labelDraft, setLabelDraft] = useState("");
  const [altTextDraft, setAltTextDraft] = useState("");
  const [userTagsDraft, setUserTagsDraft] = useState<string[]>([]);
  const [userTagInput, setUserTagInput] = useState("");
  const [usageRightsDraft, setUsageRightsDraft] = useState<MediaAsset["usage_rights"]>("unknown");
  const [assetTypeDraft, setAssetTypeDraft] = useState<MediaAsset["asset_type"]>(null);
  const [collectionDraft, setCollectionDraft] = useState<string>("");
  const labelInputRef = useRef<HTMLInputElement | null>(null);
  const altTextInputRef = useRef<HTMLInputElement | null>(null);
  const userTagsInputRef = useRef<HTMLInputElement | null>(null);
  const usageRightsRef = useRef<HTMLSelectElement | null>(null);
  const assetTypeRef = useRef<HTMLSelectElement | null>(null);
  const collectionRef = useRef<HTMLSelectElement | null>(null);
  const [searchValue, setSearchValue] = useState(searchParams.get("q") ?? "");
  const [assetType, setAssetType] = useState(searchParams.get("asset_type") ?? "");
  const [collectionId, setCollectionId] = useState(searchParams.get("collection_id") ?? "");
  const [aspectRatio, setAspectRatio] = useState(searchParams.get("aspect_ratio") ?? "");
  const [suitableFor, setSuitableFor] = useState(
    (searchParams.get("suitable_for") ?? "").split(",").map((value) => value.trim()).filter(Boolean),
  );
  const [usageRights, setUsageRights] = useState(
    (searchParams.get("usage_rights") ?? "").split(",").map((value) => value.trim()).filter(Boolean),
  );
  const [urlInput, setUrlInput] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [importUrlsModalOpen, setImportUrlsModalOpen] = useState(false);
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [rejectedUploadFiles, setRejectedUploadFiles] = useState<File[]>([]);
  const [uploadFileStatuses, setUploadFileStatuses] = useState<Record<string, "ready" | "uploading" | "uploaded" | "failed">>({});
  const [uploadFileProgress, setUploadFileProgress] = useState<Record<string, number>>({});
  const [uploadDragActive, setUploadDragActive] = useState(false);
  const [uploadValidationError, setUploadValidationError] = useState<string | null>(null);
  const [uploadResult, setUploadResult] = useState<{
    created: number;
    deduped: number;
    failed: number;
    restored: number;
  } | null>(null);
  const [uploadCollectionId, setUploadCollectionId] = useState("");
  const [uploadAssetType, setUploadAssetType] = useState("");
  const [importCollectionId, setImportCollectionId] = useState("");
  const [importAssetType, setImportAssetType] = useState("");
  const [importUrlsError, setImportUrlsError] = useState<string | null>(null);
  const [importUrlsResult, setImportUrlsResult] = useState<{
    created: number;
    deduped: number;
    failed: Array<{ url: string; reason: string }>;
  } | null>(null);
  const [shopifyModalOpen, setShopifyModalOpen] = useState(false);
  const [selectedConnectionId, setSelectedConnectionId] = useState("");

  const assets = useMemo(
    () => (mediaQuery.data?.pages.flatMap((page) => page.data) ?? []).map((asset) => ({ ...asset, ...(optimisticAssets[asset.id] ?? {}) })),
    [mediaQuery.data?.pages, optimisticAssets],
  );
  const collections = collectionsQuery.data?.data ?? [];
  const connections = shopifyIntegrations.data?.data ?? [];
  const selected = selectedAssetId ? assets.find((asset) => asset.id === selectedAssetId) ?? null : null;
  const hasShopifyConnection = connections.some((connection) => connection.status === "connected");
  const accountConnectionsHref = buildAccountConnectionsPath(brandId);
  const hasActiveFilters = Boolean(
    searchValue.trim()
      || assetType.trim()
      || collectionId.trim()
      || aspectRatio.trim()
      || suitableFor.length > 0
      || usageRights.length > 0,
  );
  const showFilterPanel = mediaQuery.isLoading || assets.length > 0 || hasActiveFilters;
  const isEmptyLibrary = !mediaQuery.isLoading && !mediaQuery.isError && assets.length === 0 && !hasActiveFilters;

  const shopifyEstimate = estimateShopifyImport.data?.data;
  const activeSearchParam = searchParams.get("q") ?? "";

  useEffect(() => {
    if (!selected) {
      setLabelDraft("");
      setAltTextDraft("");
      setUserTagsDraft([]);
      setUserTagInput("");
      setUsageRightsDraft("unknown");
      setAssetTypeDraft(null);
      setCollectionDraft("");
      return;
    }
    setLabelDraft(selected.label || "");
    setAltTextDraft(selected.alt_text || "");
    setUserTagsDraft(selected.user_tags);
    setUserTagInput("");
    setUsageRightsDraft(selected.usage_rights);
    setAssetTypeDraft(selected.asset_type);
    setCollectionDraft(selected.collection_id || "");
  }, [selected?.id, selected?.label]);

  useEffect(() => {
    if (!selected) return;
    const cleanup: Array<() => void> = [];
    const bindBlur = <T extends HTMLInputElement | HTMLSelectElement>(
      node: T | null,
      handler: () => void,
    ) => {
      if (!node) return;
      node.addEventListener("blur", handler);
      cleanup.push(() => node.removeEventListener("blur", handler));
    };

    bindBlur(labelInputRef.current, () => {
      void onSaveSelectedField("label", labelInputRef.current?.value ?? "", selected.label || "");
    });
    bindBlur(altTextInputRef.current, () => {
      void onSaveSelectedField("alt_text", altTextInputRef.current?.value ?? "", selected.alt_text || "");
    });
    bindBlur(userTagsInputRef.current, () => {
      void persistUserTags();
    });
    bindBlur(usageRightsRef.current, () => {
      void onSaveSelectedField("usage_rights", (usageRightsRef.current?.value ?? "unknown") as MediaAsset["usage_rights"], selected.usage_rights);
    });
    bindBlur(assetTypeRef.current, () => {
      const value = assetTypeRef.current?.value ?? "";
      void onSaveSelectedField("asset_type", (value || null) as MediaAsset["asset_type"], selected.asset_type);
    });
    bindBlur(collectionRef.current, () => {
      const value = collectionRef.current?.value ?? "";
      void onSaveSelectedField("collection_id", value || null, selected.collection_id);
    });

    return () => {
      for (const fn of cleanup) fn();
    };
  }, [selected?.id, selected?.label, selected?.alt_text, selected?.usage_rights, selected?.asset_type, selected?.collection_id, selected?.user_tags, userTagsDraft, userTagInput]);

  function resetShopifyModal() {
    setSelectedConnectionId("");
    estimateShopifyImport.reset();
    confirmShopifyImport.reset();
  }

  function resetUploadModal() {
    setPendingFiles([]);
    setRejectedUploadFiles([]);
    setUploadFileStatuses({});
    setUploadFileProgress({});
    setUploadDragActive(false);
    setUploadValidationError(null);
    setUploadResult(null);
    setUploadCollectionId("");
    setUploadAssetType("");
  }

  function resetImportUrlsModal() {
    setUrlInput("");
    setImportCollectionId("");
    setImportAssetType("");
    setImportUrlsError(null);
    setImportUrlsResult(null);
  }

  async function onEstimateShopifyImport() {
    if (!selectedConnectionId) return;
    await estimateShopifyImport.mutateAsync({ connection_id: selectedConnectionId });
  }

  async function onConfirmShopifyImport() {
    if (!shopifyEstimate) return;
    const result = await confirmShopifyImport.mutateAsync({
      job_preview_id: shopifyEstimate.job_preview_id,
      confirmed_cost_cents: shopifyEstimate.estimate.est_caption_cost_cents,
    });
    setShopifyModalOpen(false);
    resetShopifyModal();
    void navigate(`/brand/${brandId}/assets/imports/${result.data.id}`);
  }

  function buildFilterParams(nextSearchValue = searchValue) {
    const next = new URLSearchParams();
    if (nextSearchValue.trim()) next.set("q", nextSearchValue.trim());
    if (assetType.trim()) next.set("asset_type", assetType.trim());
    if (collectionId.trim()) next.set("collection_id", collectionId.trim());
    if (aspectRatio.trim()) next.set("aspect_ratio", aspectRatio.trim());
    if (suitableFor.length > 0) next.set("suitable_for", suitableFor.join(","));
    if (usageRights.length > 0) next.set("usage_rights", usageRights.join(","));
    return next;
  }

  function applyFilters() {
    const next = buildFilterParams();
    setSearchParams(next);
  }

  useEffect(() => {
    const nextQ = searchValue.trim();
    if (nextQ === activeSearchParam) return;
    const timer = setTimeout(() => {
      setSearchParams(buildFilterParams(nextQ));
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue, activeSearchParam, assetType, collectionId, aspectRatio, suitableFor, usageRights]);

  function toggleListValue(
    current: string[],
    setValue: (updater: (existing: string[]) => string[]) => void,
    nextValue: string,
  ) {
    setValue((existing) => (
      existing.includes(nextValue)
        ? existing.filter((value) => value !== nextValue)
        : [...existing, nextValue]
    ));
  }

  function classifyPendingFile(file: File): "ready" | "uploading" | "uploaded" | "failed" {
    const key = `${file.name}-${file.size}`;
    return uploadFileStatuses[key] ?? "ready";
  }

  function progressForPendingFile(file: File): number {
    const key = `${file.name}-${file.size}`;
    return uploadFileProgress[key] ?? 0;
  }

  function normalizeUploadFiles(files: File[]): File[] {
    const accepted = files.filter((file) => ACCEPTED_UPLOAD_MIME_TYPES.includes(file.type as typeof ACCEPTED_UPLOAD_MIME_TYPES[number]));
    const rejected = files.filter((file) => !ACCEPTED_UPLOAD_MIME_TYPES.includes(file.type as typeof ACCEPTED_UPLOAD_MIME_TYPES[number]));
    setRejectedUploadFiles(rejected);
    if (accepted.length !== files.length) {
      setUploadValidationError("Only PNG, JPEG, WebP, PDF, and MP4 files can be uploaded.");
    } else {
      setUploadValidationError(null);
    }
    setUploadResult(null);
    setUploadFileStatuses(
      Object.fromEntries(accepted.map((file) => [`${file.name}-${file.size}`, "ready" as const])),
    );
    setUploadFileProgress(
      Object.fromEntries(accepted.map((file) => [`${file.name}-${file.size}`, 0])),
    );
    return accepted;
  }

  async function onUploadFiles(files: File[]) {
    if (!files || files.length === 0) return;
    setUploadValidationError(null);
    let collectionId = uploadCollectionId || "";
    if (!collectionId) {
      const today = new Date().toISOString().slice(0, 10);
      const collection = await api.post<{ data: AssetCollection }>(`/api/brands/${brandId}/collections`, {
        label: `Upload ${today}`,
        type: "upload_batch",
      });
      collectionId = collection.data.id;
      setUploadCollectionId(collectionId);
    }

    let created = 0;
    let deduped = 0;
    let restored = 0;
    let failed = rejectedUploadFiles.length;

    for (const file of files) {
      const key = `${file.name}-${file.size}`;
      setUploadFileStatuses((current) => ({ ...current, [key]: "uploading" }));
      const form = new FormData();
      form.append("file", file);
      form.append("label", file.name);
      if (uploadAssetType) form.append("asset_type", uploadAssetType);
      form.append("collection_id", collectionId);
      try {
        const result = await upload.mutateAsync({
          formData: form,
          onProgress: (progress) => {
            setUploadFileProgress((current) => ({ ...current, [key]: progress }));
          },
        });
        created += result.data.created.length;
        deduped += result.data.deduped.length;
        restored += result.data.restored.length;
        setUploadFileProgress((current) => ({ ...current, [key]: 1 }));
        setUploadFileStatuses((current) => ({ ...current, [key]: "uploaded" }));
      } catch {
        failed += 1;
        setUploadFileStatuses((current) => ({ ...current, [key]: "failed" }));
      }
    }

    setUploadResult({
      created,
      deduped,
      failed,
      restored,
    });
    showToast({
      title: "Upload complete",
      body: `${created} created, ${deduped} deduped, ${failed} failed${restored > 0 ? `, ${restored} restored` : ""}.`,
    });
  }

  async function onImportUrls() {
    const urls = urlInput.split("\n").map((line) => line.trim()).filter(Boolean);
    if (urls.length === 0) return;
    if (urls.length > MAX_IMPORT_URLS) {
      setImportUrlsError(`You can import up to ${MAX_IMPORT_URLS} URLs at a time.`);
      return;
    }
    setImportUrlsError(null);
    const result = await importUrls.mutateAsync({
      urls,
      collection_id: importCollectionId || undefined,
      asset_type: importAssetType || undefined,
    });
    setImportUrlsResult({
      created: result.data.created.length,
      deduped: result.data.deduped.length,
      failed: result.data.failed,
    });
    showToast({
      title: "URL import complete",
      body: `${result.data.created.length} created, ${result.data.deduped.length} deduped, ${result.data.failed.length} failed.`,
    });
  }

  async function onCopyReference(assetId: string) {
    const value = `[[media:${assetId}]]`;
    await navigator.clipboard.writeText(value);
  }

  async function onSaveSelectedField<K extends keyof MediaAsset>(
    field: K,
    nextValue: MediaAsset[K],
    previousValue: MediaAsset[K],
  ) {
    if (!selected) return;
    const normalizedValue = typeof nextValue === "string"
      ? ((nextValue.trim() || null) as MediaAsset[K])
      : nextValue;
    const isEqual = JSON.stringify(normalizedValue) === JSON.stringify(previousValue);
    if (isEqual) return;

    setOptimisticAssets((current) => ({
      ...current,
      [selected.id]: {
        ...(current[selected.id] ?? {}),
        [field]: normalizedValue,
      },
    }));

    try {
      await updateAsset.mutateAsync({ assetId: selected.id, body: { [field]: normalizedValue } as Partial<MediaAsset> });
    } catch (error) {
      setOptimisticAssets((current) => ({
        ...current,
        [selected.id]: {
          ...(current[selected.id] ?? {}),
          [field]: previousValue,
        },
      }));
      if (field === "label") setLabelDraft((previousValue as string | null) || "");
      if (field === "alt_text") setAltTextDraft((previousValue as string | null) || "");
      if (field === "user_tags") {
        setUserTagsDraft((previousValue as string[]) ?? []);
        setUserTagInput("");
      }
      if (field === "usage_rights") setUsageRightsDraft(previousValue as MediaAsset["usage_rights"]);
      if (field === "asset_type") setAssetTypeDraft(previousValue as MediaAsset["asset_type"]);
      if (field === "collection_id") setCollectionDraft((previousValue as string | null) || "");
      showToast({
        title: "Asset update failed",
        body: error instanceof Error ? error.message : "The asset label could not be saved.",
      });
    }
  }

  function handleSearchInput(value: string) {
    setSearchValue(value);
  }

  function handleDraftInput(
    setter: (value: string) => void,
    value: string,
  ) {
    setter(value);
  }

  function buildNextUserTags(nextInput = userTagInput, base = userTagsDraft): string[] {
    const normalizedBase = base.map((value) => value.trim()).filter(Boolean);
    const nextTag = nextInput.trim();
    if (!nextTag) return normalizedBase;
    if (normalizedBase.includes(nextTag)) return normalizedBase;
    return [...normalizedBase, nextTag];
  }

  async function persistUserTags(nextTags = buildNextUserTags()) {
    if (!selected) return;
    setUserTagsDraft(nextTags);
    setUserTagInput("");
    await onSaveSelectedField("user_tags", nextTags, selected.user_tags);
  }

  async function removeUserTag(tag: string) {
    if (!selected) return;
    const nextTags = userTagsDraft.filter((value) => value !== tag);
    setUserTagsDraft(nextTags);
    await onSaveSelectedField("user_tags", nextTags, selected.user_tags);
  }

  if (!brandIdParam) {
    return (
      <div className="py-12 text-center">
        <h2 className="mb-2 text-xl font-bold text-slate-900">Asset Library needs a brand</h2>
        <p className="text-sm text-slate-500">Choose a brand before opening the asset library.</p>
      </div>
    );
  }

  if (brandLoading && !brand) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-brand-600 border-t-transparent" />
      </div>
    );
  }

  if (brandError || !brand) {
    return (
      <div className="mx-auto max-w-3xl py-12 text-center">
        <h2 className="text-2xl font-semibold text-slate-900">This brand could not be opened</h2>
        <p className="mt-3 text-sm leading-6 text-slate-500">
          The brand may have been removed, the link may be stale, or you may no longer have access.
        </p>
        <div className="mt-6 flex items-center justify-center gap-3">
          <Button variant="secondary" onClick={() => navigate("/brand")}>Choose brand</Button>
          <Button onClick={() => navigate("/home")}>Back to workspace</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <WorkspaceHeader
        eyebrow="Brand Workspace"
        title="Asset Library"
        description={`Search, organize, and reuse brand-approved media for ${brand.name}.`}
        breadcrumbs={[
          { label: brand.name, href: `/brand/${brandId}` },
          { label: "Assets" },
        ]}
        metadata={(
          <>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {assets.length} visible assets
            </span>
            <span className="rounded-full bg-white px-2.5 py-1 ring-1 ring-slate-200">
              {collections.length} collections
            </span>
          </>
        )}
        actions={(
          <>
            <Link
              to={`/brand/${brandId}/assets/imports`}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
            >
              View imports
            </Link>
            <button
              type="button"
              onClick={() => {
                resetUploadModal();
                setUploadModalOpen(true);
              }}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
            >
              Upload assets
            </button>
            <button
              type="button"
              onClick={() => {
                resetImportUrlsModal();
                setImportUrlsModalOpen(true);
              }}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
            >
              Import URLs
            </button>
            {hasShopifyConnection ? (
              <button
                type="button"
                onClick={() => {
                  resetShopifyModal();
                  setShopifyModalOpen(true);
                }}
                className="rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-medium text-emerald-700"
              >
                Import from Shopify
              </button>
            ) : null}
          </>
        )}
      />

      {showFilterPanel ? (
      <section className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="flex flex-wrap items-end gap-3">
          <div className="min-w-[220px] flex-1">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Search</label>
            <input
              value={searchValue}
              onChange={(e) => handleSearchInput(e.target.value)}
              onInput={(e) => handleSearchInput((e.target as HTMLInputElement).value)}
              placeholder="outdoor, founder, launch..."
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
          </div>
          <div className="w-[180px]">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Type</label>
            <select
              value={assetType}
              onChange={(e) => setAssetType(e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">All</option>
              <option value="product_shot">Product shot</option>
              <option value="lifestyle">Lifestyle</option>
              <option value="ugc">UGC</option>
              <option value="brand_kit">Brand kit</option>
              <option value="social">Social</option>
            </select>
          </div>
          <div className="w-[220px]">
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Collection</label>
            <select
              value={collectionId}
              onChange={(e) => setCollectionId(e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">All assets</option>
              {collections.map((collection) => (
                <option key={collection.id} value={collection.id}>{collection.label}</option>
              ))}
            </select>
          </div>
          <button
            type="button"
            onClick={applyFilters}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
          >
            Apply
          </button>
          <button
            type="button"
            onClick={() => {
              setSearchValue("");
              setAssetType("");
              setCollectionId("");
              setAspectRatio("");
              setSuitableFor([]);
              setUsageRights([]);
              setSearchParams(new URLSearchParams());
            }}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
          >
            Clear
          </button>
        </div>
        <div className="mt-4 space-y-4">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Aspect ratio</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {CANONICAL_ASPECT_RATIOS.map((ratio) => (
                <button
                  key={ratio}
                  type="button"
                  onClick={() => setAspectRatio((current) => current === ratio ? "" : ratio)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                    aspectRatio === ratio
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-700"
                  }`}
                >
                  {ratio}
                </button>
              ))}
            </div>
          </div>
          <div className="grid gap-4 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Suitable for</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {SUITABLE_FOR_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleListValue(suitableFor, setSuitableFor, option)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                    suitableFor.includes(option)
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-700"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Usage rights</p>
            <div className="mt-2 flex flex-wrap gap-2">
              {USAGE_RIGHTS_OPTIONS.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => toggleListValue(usageRights, setUsageRights, option)}
                  className={`rounded-full border px-3 py-1.5 text-xs font-medium ${
                    usageRights.includes(option)
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-300 text-slate-700"
                  }`}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>
        </div>
        </div>
      </section>
      ) : null}

      {mediaQuery.isError && (
        <div role="alert" className="rounded-lg border border-amber-300 bg-amber-50 p-4">
          <p>Assets could not be loaded. Search may be temporarily unavailable.</p>
          <button type="button" onClick={() => void mediaQuery.refetch()} className="mt-2 underline">Retry loading assets</button>
        </div>
      )}
      {isEmptyLibrary ? (
        <section
          data-testid="asset-empty-state"
          className="rounded-3xl border border-dashed border-slate-300 bg-white px-6 py-10 text-center shadow-sm"
        >
          <p className="font-mono text-[11px] uppercase tracking-[0.16em] text-slate-500">Start the library</p>
          <h2 className="mt-2 text-2xl font-semibold text-slate-950">Add brand-approved media first</h2>
          <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600">
            Upload product shots, campaign images, brand-kit files, or paste image URLs. Filters and collections appear after the first asset is available.
          </p>
          {!hasShopifyConnection ? (
            <p className="mx-auto mt-3 max-w-2xl text-sm leading-6 text-slate-600">
              Need storefront media too? Connect or assign Shopify from{" "}
              <Link to={accountConnectionsHref} className="font-medium text-emerald-700 underline underline-offset-4">
                account connections
              </Link>
              {" "}first, then return here to import into this brand library.
            </p>
          ) : null}
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button
              type="button"
              onClick={() => {
                resetUploadModal();
                setUploadModalOpen(true);
              }}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Upload assets
            </button>
            <button
              type="button"
              onClick={() => {
                resetImportUrlsModal();
                setImportUrlsModalOpen(true);
              }}
              className="rounded-lg border border-slate-300 bg-white px-4 py-2 text-sm font-medium text-slate-700"
            >
              Import image URLs
            </button>
            {hasShopifyConnection ? (
              <button
                type="button"
                onClick={() => {
                  resetShopifyModal();
                  setShopifyModalOpen(true);
                }}
                className="rounded-lg border border-emerald-300 bg-white px-4 py-2 text-sm font-medium text-emerald-700"
              >
                Import from Shopify
              </button>
            ) : null}
          </div>
        </section>
      ) : (
      <section className="grid gap-6 lg:grid-cols-[1.2fr,0.8fr]">
        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Ingest</p>
            <h2 className="mt-1 text-lg font-semibold text-slate-900">Add assets</h2>
            <p className="mt-3 text-sm text-slate-600">
              Use the header actions to upload local files or import external image URLs{hasShopifyConnection ? ", or start a Shopify import into this library" : ""}.
            </p>
            {!hasShopifyConnection ? (
              <p className="mt-3 text-sm text-slate-600">
                Shopify imports are managed from account connections, then assigned back to this brand for library access.{" "}
                <Link to={accountConnectionsHref} className="font-medium text-emerald-700 underline underline-offset-4">
                  Manage account connections
                </Link>
              </p>
            ) : null}
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="mb-4 flex gap-3 overflow-x-auto pb-1">
              <button
                type="button"
                onClick={() => {
                  setCollectionId("");
                  const next = new URLSearchParams(searchParams);
                  next.delete("collection_id");
                  setSearchParams(next);
                }}
                className={`min-w-[140px] rounded-xl border p-3 text-left ${!collectionId ? "border-slate-900 bg-slate-900 text-white" : "border-slate-200 bg-slate-50 text-slate-700"}`}
              >
                <p className="text-sm font-medium">All assets</p>
                <p className="mt-1 text-xs opacity-80">{assets.length} shown</p>
              </button>
              {collections.map((collection) => (
                <button
                  key={collection.id}
                  type="button"
                  onClick={() => {
                    const nextValue = collectionId === collection.id ? "" : collection.id;
                    setCollectionId(nextValue);
                    const next = new URLSearchParams(searchParams);
                    if (nextValue) next.set("collection_id", nextValue);
                    else next.delete("collection_id");
                    setSearchParams(next);
                  }}
                  className={`min-w-[180px] rounded-xl border p-3 text-left ${collectionId === collection.id ? "border-blue-600 bg-blue-50 text-blue-900" : "border-slate-200 bg-slate-50 text-slate-700"}`}
                >
                  {collection.cover_asset_id ? (
                    <img
                      src={collection.cover_url || apiUrl(`/api/brands/${brandId}/media/${collection.cover_asset_id}/file`)}
                      alt={`${collection.label} cover`}
                      loading="lazy"
                      className="h-12 w-full rounded-lg bg-slate-100 object-cover"
                    />
                  ) : (
                    <div className="h-12 rounded-lg bg-slate-100" />
                  )}
                  <p className="mt-2 text-sm font-medium">{collection.label}</p>
                  <p className="mt-1 text-xs opacity-80">{collection.asset_count} assets</p>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Library</p>
                <h2 className="mt-1 text-lg font-semibold text-slate-900">{assets.length} assets</h2>
              </div>
              <div className="text-xs text-slate-500">
                {collections.length} collections
              </div>
            </div>

            <div className="mt-4 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {assets.map((asset) => (
                <button
                  key={asset.id}
                  type="button"
                  onClick={() => setSelectedAssetId(asset.id)}
                  className="rounded-xl border border-slate-200 p-3 text-left hover:border-slate-400"
                >
                  <MediaPreview compact mimeType={asset.mime_type} url={asset.thumbnail_url || apiUrl(`/api/brands/${brandId}/media/${asset.id}/file`)} label={asset.alt_text || asset.label || "Brand asset"} />
                  <div className="mt-3">
                    <p className="text-sm font-medium text-slate-900">{asset.label || asset.description || "Brand asset"}</p>
                    <p className="mt-1 text-xs text-slate-500">{asset.asset_type || "untyped"} · {asset.aspect_ratio || "?"}</p>
                    <p className="mt-1 text-xs text-slate-500">{asset.processing_status}</p>
                  </div>
                </button>
              ))}
            </div>
            {mediaQuery.hasNextPage ? (
              <div className="mt-4 flex justify-center">
                <button
                  type="button"
                  onClick={() => void mediaQuery.fetchNextPage()}
                  disabled={mediaQuery.isFetchingNextPage}
                  className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {mediaQuery.isFetchingNextPage ? "Loading…" : "Load more"}
                </button>
              </div>
            ) : null}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-5">
            <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Reference preview</p>
            <textarea
              value={previewText}
              onChange={(e) => setPreviewText(e.target.value)}
              placeholder="Write draft text with [[media:...]] references"
              className="mt-3 min-h-[120px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={() => void renderPreview.mutateAsync({ text: previewText, context: "html" })}
              className="mt-3 rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white"
            >
              Render preview
            </button>
            {renderPreview.data?.data?.resolved ? (
              <div
                className="prose prose-sm mt-4 max-w-none rounded-lg border border-slate-200 p-3"
                dangerouslySetInnerHTML={{ __html: safePreviewHtml(renderPreview.data.data.resolved) }}
              />
            ) : null}
          </div>
        </div>
      </section>
      )}

      {selected ? (
        <div
          className="fixed inset-0 z-50"
          aria-label="Asset details overlay"
        >
          <button
            type="button"
            aria-label="Close asset details"
            data-testid="asset-panel-backdrop"
            className="absolute inset-0 bg-slate-950/30 backdrop-blur-sm"
            onClick={() => setSelectedAssetId(null)}
          />
          <div className="absolute inset-y-0 right-0 flex w-full max-w-xl">
            <section
              className="relative ml-auto flex h-full w-full max-w-xl flex-col overflow-y-auto border-l border-slate-200 bg-white p-6 shadow-2xl"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Inspector</p>
                  <h2 className="mt-1 text-lg font-semibold text-slate-900">{selected.label || selected.description || "Brand asset"}</h2>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedAssetId(null)}
                  className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
                >
                  Close
                </button>
              </div>

              <MediaPreview mimeType={selected.mime_type} url={selected.thumbnail_url || apiUrl(`/api/brands/${brandId}/media/${selected.id}/file`)} label={selected.alt_text || selected.label || "Brand asset"} />

              <div className="mt-4 space-y-4">
                <label className="block">
                  <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Label</span>
                  <input
                    data-testid="asset-panel-label-input"
                    ref={labelInputRef}
                    value={labelDraft}
                    onChange={(e) => handleDraftInput(setLabelDraft, e.target.value)}
                    onInput={(e) => handleDraftInput(setLabelDraft, (e.target as HTMLInputElement).value)}
                    className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                  />
                </label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Alt text</span>
                    <input
                      data-testid="asset-panel-alt-text-input"
                      ref={altTextInputRef}
                      value={altTextDraft}
                      onChange={(e) => handleDraftInput(setAltTextDraft, e.target.value)}
                      onInput={(e) => handleDraftInput(setAltTextDraft, (e.target as HTMLInputElement).value)}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    />
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">User tags</span>
                    <div className="mt-2 rounded-lg border border-slate-300 px-3 py-2">
                      <div className="flex flex-wrap gap-2">
                        {userTagsDraft.map((tag) => (
                          <span
                            key={tag}
                            data-testid="asset-panel-user-tag-chip"
                            className="inline-flex items-center gap-2 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-700"
                          >
                            <span>{tag}</span>
                            <button
                              type="button"
                              aria-label={`Remove ${tag}`}
                              onClick={() => void removeUserTag(tag)}
                              className="rounded-full text-slate-500 hover:text-slate-700"
                            >
                              ×
                            </button>
                          </span>
                        ))}
                      </div>
                      <div className="mt-2 flex gap-2">
                        <input
                          data-testid="asset-panel-user-tags-input"
                          ref={userTagsInputRef}
                          value={userTagInput}
                          onChange={(e) => handleDraftInput(setUserTagInput, e.target.value)}
                          onInput={(e) => handleDraftInput(setUserTagInput, (e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => {
                            if (e.key !== "Enter") return;
                            e.preventDefault();
                            void persistUserTags();
                          }}
                          placeholder="Add tag"
                          className="flex-1 border-0 bg-transparent px-0 py-0 text-sm outline-none"
                        />
                        <button
                          type="button"
                          onClick={() => void persistUserTags()}
                          className="rounded-lg border border-slate-300 px-2.5 py-1 text-xs font-medium text-slate-700"
                        >
                          Add
                        </button>
                      </div>
                    </div>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Usage rights</span>
                    <select
                      data-testid="asset-panel-usage-rights-select"
                      ref={usageRightsRef}
                      value={usageRightsDraft}
                      onChange={(e) => setUsageRightsDraft(e.target.value as MediaAsset["usage_rights"])}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    >
                      {USAGE_RIGHTS_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Asset type</span>
                    <select
                      data-testid="asset-panel-asset-type-select"
                      ref={assetTypeRef}
                      value={assetTypeDraft ?? ""}
                      onChange={(e) => setAssetTypeDraft((e.target.value || null) as MediaAsset["asset_type"])}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="">untyped</option>
                      {ASSET_TYPE_OPTIONS.map((option) => (
                        <option key={option} value={option}>{option}</option>
                      ))}
                    </select>
                  </label>
                  <label className="block sm:col-span-2">
                    <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Collection</span>
                    <select
                      data-testid="asset-panel-collection-select"
                      ref={collectionRef}
                      value={collectionDraft}
                      onChange={(e) => setCollectionDraft(e.target.value)}
                      className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
                    >
                      <option value="">No collection</option>
                      {collections.map((collection) => (
                        <option key={collection.id} value={collection.id}>{collection.label}</option>
                      ))}
                    </select>
                  </label>
                </div>

                <div className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs text-slate-500">Aspect ratio</p>
                    <p className="mt-1 text-sm font-medium text-slate-900">{selected.aspect_ratio || "?"}</p>
                  </div>
                  <div className="rounded-xl border border-slate-200 p-3">
                    <p className="text-xs text-slate-500">Processing</p>
                    <p className="mt-1 text-sm font-medium text-slate-900">{selected.processing_status}</p>
                  </div>
                </div>

                <section className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">AI metadata</p>
                  <div className="mt-3 space-y-3 text-sm text-slate-700">
                    <div>
                      <p className="text-xs text-slate-500">Description</p>
                      <p className="mt-1">{selected.description || "No description yet"}</p>
                    </div>
                    <div className="grid gap-3 sm:grid-cols-2">
                      <div>
                        <p className="text-xs text-slate-500">Auto tags</p>
                        <p className="mt-1">{selected.auto_tags.join(", ") || "None"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Suitable for</p>
                        <p className="mt-1">{selected.suitable_for.join(", ") || "None"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Scene type</p>
                        <p className="mt-1">{selected.scene_type || "Unknown"}</p>
                      </div>
                      <div>
                        <p className="text-xs text-slate-500">Dominant colors</p>
                        <p className="mt-1">{selected.dominant_colors.join(", ") || "None"}</p>
                      </div>
                    </div>
                  </div>
                </section>

                <section className="rounded-xl border border-slate-200 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Provenance</p>
                  <div className="mt-3 space-y-3 text-sm text-slate-700">
                    <div>
                      <p className="text-xs text-slate-500">Primary source</p>
                      <p className="mt-1">{selected.primary_source}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500">Source URLs</p>
                      <div className="mt-1 flex flex-col gap-1">
                        {selected.source_urls.length > 0 ? selected.source_urls.map((url) => (
                          <a key={url} href={url} target="_blank" rel="noreferrer" className="break-all text-blue-700 underline">
                            {url}
                          </a>
                        )) : <p>None</p>}
                      </div>
                    </div>
                    {selected.shopify_product_id ? (
                      <div>
                        <p className="text-xs text-slate-500">Shopify product</p>
                        <Link
                          to={`/corpus?brandId=${encodeURIComponent(brandId)}&shopifyProductId=${encodeURIComponent(selected.shopify_product_id)}`}
                          data-testid="asset-panel-shopify-product-link"
                          className="mt-1 inline-flex text-blue-700 underline"
                        >
                          {selected.shopify_product_id}
                        </Link>
                      </div>
                    ) : null}
                  </div>
                </section>

                <div className="flex flex-wrap gap-2">
                  <button
                    type="button"
                    disabled={!selected.mime_type.startsWith("image/")}
                    title={selected.mime_type.startsWith("image/") ? undefined : "Attach this file from the draft review page"}
                    onClick={() => void onCopyReference(selected.id)}
                    className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700"
                  >
                    Copy reference
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (!window.confirm("Delete this asset?")) return;
                      void deleteAsset.mutateAsync(selected.id).then(() => setSelectedAssetId(null));
                    }}
                    className="rounded-lg border border-red-300 px-3 py-2 text-sm text-red-700"
                  >
                    Delete
                  </button>
                </div>

                <p className="text-xs text-slate-500 break-all">{selected.id}</p>
              </div>
            </section>
          </div>
        </div>
      ) : null}

      <Modal
        open={uploadModalOpen}
        onClose={() => {
          setUploadModalOpen(false);
          resetUploadModal();
        }}
        title="Upload assets"
        className="sm:max-w-2xl"
      >
        <div className="space-y-4">
          <label
            data-testid="upload-dropzone"
            className={`block rounded-2xl border border-dashed p-6 text-center ${
              uploadDragActive
                ? "border-slate-900 bg-slate-100"
                : "border-slate-300 bg-slate-50"
            }`}
            onDragOver={(event) => {
              event.preventDefault();
              setUploadDragActive(true);
            }}
            onDragEnter={(event) => {
              event.preventDefault();
              setUploadDragActive(true);
            }}
            onDragLeave={(event) => {
              event.preventDefault();
              if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
              setUploadDragActive(false);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setUploadDragActive(false);
              const dropped = Array.from(event.dataTransfer?.files ?? []);
              setPendingFiles(normalizeUploadFiles(dropped));
            }}
          >
            <span className="text-sm font-medium text-slate-900">Drop files here or choose files</span>
            <span className="mt-2 block text-xs text-slate-500">PNG, JPEG, WebP, PDF, and MP4 supported.</span>
            <input
              data-testid="upload-modal-input"
              type="file"
              multiple
              accept="image/png,image/jpeg,image/webp,application/pdf,video/mp4"
              className="sr-only"
              onChange={(e) => setPendingFiles(normalizeUploadFiles(Array.from(e.target.files ?? [])))}
            />
          </label>
          {uploadValidationError ? (
            <div
              data-testid="upload-validation-error"
              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {uploadValidationError}
            </div>
          ) : null}

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Collection</span>
              <select
                value={uploadCollectionId}
                onChange={(e) => setUploadCollectionId(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Create upload collection automatically</option>
                {collections.map((collection) => (
                  <option key={collection.id} value={collection.id}>{collection.label}</option>
                ))}
              </select>
              {!uploadCollectionId ? (
                <p
                  data-testid="upload-default-collection-note"
                  className="mt-2 text-xs text-slate-500"
                >
                  New files will be grouped into <span className="font-medium text-slate-700">Upload {new Date().toISOString().slice(0, 10)}</span>.
                </p>
              ) : (
                <p
                  data-testid="upload-active-collection-note"
                  className="mt-2 text-xs text-slate-500"
                >
                  Uploading into the selected collection.
                </p>
              )}
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Asset type</span>
              <select
                value={uploadAssetType}
                onChange={(e) => setUploadAssetType(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Unspecified</option>
                {ASSET_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="space-y-3">
            {pendingFiles.length === 0 ? (
              <p className="text-sm text-slate-500">No files selected yet.</p>
            ) : pendingFiles.map((file) => (
              <div key={`${file.name}-${file.size}`} className="flex items-center gap-3 rounded-xl border border-slate-200 p-3">
                <div className="h-12 w-12 overflow-hidden rounded-lg bg-slate-100">
                  <PendingMediaPreview file={file} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-slate-900">{file.name}</p>
                  <p className="mt-1 text-xs text-slate-500">{file.type || "application/octet-stream"} · {file.size} bytes</p>
                </div>
                <span
                  data-testid="upload-file-status"
                  className={`rounded-full px-2.5 py-1 text-[11px] font-medium ${
                    classifyPendingFile(file) === "uploaded"
                      ? "bg-emerald-100 text-emerald-700"
                      : classifyPendingFile(file) === "uploading"
                        ? "bg-amber-100 text-amber-700"
                        : classifyPendingFile(file) === "failed"
                          ? "bg-red-100 text-red-700"
                      : "bg-slate-100 text-slate-700"
                  }`}
                >
                  {classifyPendingFile(file) === "uploaded"
                    ? "Uploaded"
                    : classifyPendingFile(file) === "uploading"
                      ? `Uploading ${Math.round(progressForPendingFile(file) * 100)}%`
                      : classifyPendingFile(file) === "failed"
                        ? "Failed"
                      : "Ready"}
                </span>
              </div>
            ))}
            {rejectedUploadFiles.map((file) => (
              <div key={`${file.name}-${file.size}-rejected`} className="flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-3">
                <div className="h-12 w-12 rounded-lg bg-red-100" />
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-red-900">{file.name}</p>
                  <p className="mt-1 text-xs text-red-700">{file.type || "application/octet-stream"} · {file.size} bytes</p>
                </div>
                <span className="rounded-full bg-red-100 px-2.5 py-1 text-[11px] font-medium text-red-700">
                  Rejected
                </span>
              </div>
            ))}
          </div>
          {uploadResult ? (
            <div
              data-testid="upload-result"
              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <p className="text-sm font-medium text-slate-900">
                {uploadResult.created} created, {uploadResult.deduped} deduped, {uploadResult.failed} failed
              </p>
              {uploadResult.restored > 0 ? (
                <p className="mt-2 text-sm text-slate-600">{uploadResult.restored} restored from soft-deleted matches.</p>
              ) : null}
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                setUploadModalOpen(false);
                resetUploadModal();
              }}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={pendingFiles.length === 0 || upload.isPending}
              onClick={() => void onUploadFiles(pendingFiles)}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {upload.isPending ? "Uploading…" : "Upload files"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={importUrlsModalOpen}
        onClose={() => {
          setImportUrlsModalOpen(false);
          resetImportUrlsModal();
        }}
        title="Import image URLs"
        className="sm:max-w-2xl"
      >
        <div className="space-y-4">
          <textarea
            data-testid="import-urls-textarea"
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            onInput={(e) => setUrlInput((e.target as HTMLTextAreaElement).value)}
            placeholder="https://example.com/image-1.png&#10;https://example.com/image-2.jpg"
            className="min-h-[160px] w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
          />

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Collection</span>
              <select
                value={importCollectionId}
                onChange={(e) => setImportCollectionId(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Create import collection automatically</option>
                {collections.map((collection) => (
                  <option key={collection.id} value={collection.id}>{collection.label}</option>
                ))}
              </select>
            </label>
            <label className="block">
              <span className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Asset type</span>
              <select
                value={importAssetType}
                onChange={(e) => setImportAssetType(e.target.value)}
                className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
              >
                <option value="">Unspecified</option>
                {ASSET_TYPE_OPTIONS.map((option) => (
                  <option key={option} value={option}>{option}</option>
                ))}
              </select>
            </label>
          </div>

          <p className="text-xs text-slate-500">Paste up to 50 image URLs, one per line.</p>
          {importUrlsError ? (
            <div
              data-testid="import-urls-error"
              className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
            >
              {importUrlsError}
            </div>
          ) : null}
          {importUrlsResult ? (
            <div
              data-testid="import-urls-result"
              className="rounded-xl border border-slate-200 bg-slate-50 p-4"
            >
              <p className="text-sm font-medium text-slate-900">
                {importUrlsResult.created} created, {importUrlsResult.deduped} deduped, {importUrlsResult.failed.length} failed
              </p>
              {importUrlsResult.failed.length > 0 ? (
                <ul className="mt-3 space-y-2 text-sm text-slate-700">
                  {importUrlsResult.failed.map((failure) => (
                    <li key={`${failure.url}-${failure.reason}`} className="rounded-lg border border-slate-200 bg-white px-3 py-2">
                      <p className="break-all font-medium text-slate-900">{failure.url}</p>
                      <p className="mt-1 text-xs uppercase tracking-[0.14em] text-slate-500">{failure.reason}</p>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="mt-3 text-sm text-slate-600">No failed URLs in this batch.</p>
              )}
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                setImportUrlsModalOpen(false);
                resetImportUrlsModal();
              }}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={urlInput.trim().length === 0 || importUrls.isPending}
              onClick={() => void onImportUrls()}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
            >
              {importUrls.isPending ? "Importing…" : "Import URLs"}
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        open={shopifyModalOpen}
        onClose={() => {
          setShopifyModalOpen(false);
          resetShopifyModal();
        }}
        title="Import from Shopify"
        className="sm:max-w-2xl"
      >
        <div className="space-y-4">
          <div>
            <label className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Shopify connection</label>
            <select
              value={selectedConnectionId}
              onChange={(e) => setSelectedConnectionId(e.target.value)}
              className="mt-2 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"
            >
              <option value="">Select a connected store</option>
              {connections.map((connection) => (
                <option key={connection.id} value={connection.id}>
                  {connection.shop || connection.platform_username || connection.id}
                </option>
              ))}
            </select>
            <p className="mt-2 text-xs text-slate-500">
              The import creates searchable product knowledge and a Shopify collection in your Asset Library.
            </p>
          </div>

          {connections.length === 0 ? (
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
              No Shopify connections are assigned to this brand yet. Connect or reassign Shopify from{" "}
              <Link to={buildAccountConnectionsPath(brandId)} className="font-medium underline">
                account connections
              </Link>.
            </div>
          ) : null}

          {estimateShopifyImport.error instanceof ApiError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {estimateShopifyImport.error.message}
            </div>
          ) : null}

          {confirmShopifyImport.error instanceof ApiError ? (
            <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-800">
              {confirmShopifyImport.error.message}
            </div>
          ) : null}

          {shopifyEstimate ? (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Estimate</p>
              <div className="mt-3 grid gap-3 sm:grid-cols-3">
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500">Products</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{shopifyEstimate.estimate.product_count}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500">Estimated images</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{shopifyEstimate.estimate.est_image_count}</p>
                </div>
                <div className="rounded-xl border border-slate-200 bg-white p-4">
                  <p className="text-xs text-slate-500">Raw import cost</p>
                  <p className="mt-1 text-xl font-semibold text-slate-900">{formatCents(shopifyEstimate.estimate.est_caption_cost_cents)}</p>
                </div>
              </div>
              <p className="mt-3 text-sm text-slate-600">
                This flow tracks raw cents per import. Billing maps those costs to credits later.
              </p>
            </div>
          ) : null}

          <div className="flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => {
                setShopifyModalOpen(false);
                resetShopifyModal();
              }}
              className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700"
            >
              Close
            </button>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => void onEstimateShopifyImport()}
                disabled={!selectedConnectionId || estimateShopifyImport.isPending}
                className="rounded-lg border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {estimateShopifyImport.isPending ? "Estimating…" : "Estimate import"}
              </button>
              <button
                type="button"
                onClick={() => void onConfirmShopifyImport()}
                disabled={!shopifyEstimate || confirmShopifyImport.isPending}
                className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                {confirmShopifyImport.isPending ? "Starting…" : "Start import"}
              </button>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
